import { getApp, getApps, initializeApp } from 'firebase/app';
import { addDoc, collection, getFirestore, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    (process.env.REACT_APP_FIREBASE_PROJECT_ID
      ? `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.appspot.com`
      : ''),
};

const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId', 'storageBucket'];

const isFirebaseReady = requiredKeys.every((key) => {
  const value = firebaseConfig[key];
  return typeof value === 'string' && value.trim() !== '' && value.trim() !== '##';
});

const app = isFirebaseReady ? (getApps().length ? getApp() : initializeApp(firebaseConfig)) : null;
const db = app ? getFirestore(app) : null;
const storage = app ? getStorage(app) : null;

export const isJobFormBackendReady = Boolean(db && storage);

export const getJobFormSetupMessage = () =>
  'Job form backend is not configured. Set REACT_APP_FIREBASE_* values (including storage bucket) and restart.';

const sanitizeFileName = (name) => name.replace(/[^a-zA-Z0-9._-]/g, '_');

export const uploadResumeFile = async (file, meta) => {
  if (!storage) throw new Error(getJobFormSetupMessage());
  if (!file) throw new Error('Resume file is required.');

  const safeFileName = sanitizeFileName(file.name || 'resume');
  const time = Date.now();
  const role = (meta?.role || 'general').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const candidate = (meta?.candidateName || 'candidate').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const filePath = `job-applications/${role}/${candidate}-${time}-${safeFileName}`;
  const fileRef = ref(storage, filePath);

  await uploadBytes(fileRef, file, {
    contentType: file.type || 'application/octet-stream',
  });
  const downloadUrl = await getDownloadURL(fileRef);

  return {
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    path: filePath,
    downloadUrl,
  };
};

export const createJobApplication = async (application) => {
  if (!db) throw new Error(getJobFormSetupMessage());

  const payload = {
    ...application,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    source: 'career_form_v1',
    status: 'new',
  };

  const refDoc = await addDoc(collection(db, 'jobApplications'), payload);
  return refDoc.id;
};
